/**
 * Filtering + grouping state for the ViewAllLinksModal.
 *
 * Extracted from ViewAllLinksModal.tsx (Sub-PR 6.4).
 */

import { useCallback, useMemo, useState } from 'react'
import type { CaseEndpoint, EndpointRelationship } from '../../../types/case-container'
import type { DirectionFilter, RelationshipGroup } from './types'

interface UseViewAllLinksFiltersOptions {
  endpoint: CaseEndpoint
  relationships: EndpointRelationship[]
  endpoints: CaseEndpoint[]
}

interface UseViewAllLinksFiltersResult {
  searchTerm: string
  setSearchTerm: (s: string) => void
  directionFilter: DirectionFilter
  setDirectionFilter: (d: DirectionFilter) => void
  statusFilter: string
  setStatusFilter: (s: string) => void
  typeFilter: string
  setTypeFilter: (s: string) => void
  expandedGroups: Set<string>
  toggleGroup: (key: string) => void
  uniqueStatuses: string[]
  uniqueTypes: string[]
  relevantRelationships: EndpointRelationship[]
  filteredRelationships: EndpointRelationship[]
  groupedRelationships: RelationshipGroup[]
  getEndpoint: (id: string) => CaseEndpoint | undefined
}

function matchesSearch(
  rel: EndpointRelationship,
  otherEndpoint: CaseEndpoint | undefined,
  searchLower: string
): boolean {
  const matchesHostname = otherEndpoint?.hostname.toLowerCase().includes(searchLower)
  const matchesLabel = rel.label?.toLowerCase().includes(searchLower)
  const matchesNotes = rel.investigationNotes?.toLowerCase().includes(searchLower)
  return Boolean(matchesHostname ?? matchesLabel ?? matchesNotes)
}

interface FilterCriteria {
  directionFilter: DirectionFilter
  statusFilter: string
  typeFilter: string
  searchTerm: string
  endpointId: string
  getEndpoint: (id: string) => CaseEndpoint | undefined
}

function passesDirection(
  rel: EndpointRelationship,
  direction: DirectionFilter,
  endpointId: string
): boolean {
  if (direction === 'outgoing' && rel.sourceEndpointId !== endpointId) return false
  if (direction === 'incoming' && rel.targetEndpointId !== endpointId) return false
  return true
}

function relationshipMatches(rel: EndpointRelationship, criteria: FilterCriteria): boolean {
  if (!passesDirection(rel, criteria.directionFilter, criteria.endpointId)) return false
  if (criteria.statusFilter !== 'all' && rel.status !== criteria.statusFilter) return false
  if (criteria.typeFilter !== 'all' && rel.connectionType !== criteria.typeFilter) return false
  if (!criteria.searchTerm) return true
  const otherId =
    rel.sourceEndpointId === criteria.endpointId ? rel.targetEndpointId : rel.sourceEndpointId
  return matchesSearch(rel, criteria.getEndpoint(otherId), criteria.searchTerm.toLowerCase())
}

function buildGroups(
  rels: EndpointRelationship[],
  endpointId: string,
  getEndpoint: (id: string) => CaseEndpoint | undefined
): RelationshipGroup[] {
  const groups = new Map<string, RelationshipGroup>()
  for (const rel of rels) {
    const isOutgoing = rel.sourceEndpointId === endpointId
    const sourceId = rel.sourceEndpointId
    const targetId = rel.targetEndpointId
    const groupKey = `${isOutgoing ? 'out' : 'in'}-${sourceId}-${rel.connectionType}-${rel.status}`
    let group = groups.get(groupKey)
    if (!group) {
      group = {
        key: groupKey,
        isOutgoing,
        sourceEndpoint: getEndpoint(sourceId),
        connectionType: rel.connectionType,
        status: rel.status,
        relationships: [],
        targetEndpoints: [],
      }
      groups.set(groupKey, group)
    }
    group.relationships.push(rel)
    group.targetEndpoints.push(getEndpoint(targetId))
  }
  return Array.from(groups.values()).sort((first, second) => {
    if (first.isOutgoing !== second.isOutgoing) return first.isOutgoing ? -1 : 1
    const firstSource = first.sourceEndpoint?.hostname ?? ''
    const secondSource = second.sourceEndpoint?.hostname ?? ''
    if (firstSource !== secondSource) return firstSource.localeCompare(secondSource)
    return first.connectionType.localeCompare(second.connectionType)
  })
}

function buildFilterCriteria(
  filters: Pick<FilterCriteria, 'directionFilter' | 'statusFilter' | 'typeFilter' | 'searchTerm'>,
  endpointId: string,
  getEndpoint: (id: string) => CaseEndpoint | undefined
): FilterCriteria {
  return { ...filters, endpointId, getEndpoint }
}

function toggleSetMember(prev: Set<string>, key: string): Set<string> {
  const next = new Set(prev)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

export function useViewAllLinksFilters({
  endpoint,
  relationships,
  endpoints,
}: UseViewAllLinksFiltersOptions): UseViewAllLinksFiltersResult {
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const getEndpoint = useCallback(
    (id: string): CaseEndpoint | undefined => endpoints.find((ep) => ep.id === id),
    [endpoints]
  )

  const relevantRelationships = useMemo(
    () =>
      relationships.filter(
        (rel) => rel.sourceEndpointId === endpoint.id || rel.targetEndpointId === endpoint.id
      ),
    [relationships, endpoint.id]
  )

  const uniqueStatuses = useMemo(
    () => Array.from(new Set(relevantRelationships.map((rel) => rel.status))).sort(),
    [relevantRelationships]
  )

  const uniqueTypes = useMemo(
    () => Array.from(new Set(relevantRelationships.map((rel) => rel.connectionType))).sort(),
    [relevantRelationships]
  )

  const filteredRelationships = useMemo(() => {
    const criteria = buildFilterCriteria(
      { directionFilter, statusFilter, typeFilter, searchTerm },
      endpoint.id,
      getEndpoint
    )
    return relevantRelationships.filter((rel) => relationshipMatches(rel, criteria))
  }, [relevantRelationships, directionFilter, statusFilter, typeFilter, searchTerm, endpoint.id, getEndpoint])

  const groupedRelationships = useMemo(
    () => buildGroups(filteredRelationships, endpoint.id, getEndpoint),
    [filteredRelationships, endpoint.id, getEndpoint]
  )

  const toggleGroup = useCallback((groupKey: string): void => {
    setExpandedGroups((prev) => toggleSetMember(prev, groupKey))
  }, [])

  return {
    searchTerm,
    setSearchTerm,
    directionFilter,
    setDirectionFilter,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    expandedGroups,
    toggleGroup,
    uniqueStatuses,
    uniqueTypes,
    relevantRelationships,
    filteredRelationships,
    groupedRelationships,
    getEndpoint,
  }
}
